/**
 * 翻前查表。见 docs/ARCHITECTURE.md §10.3。
 *
 * 翻前最小的精确组合数是 2.1e9（N=1），比 250ms 预算大四个数量级，
 * 蒙特卡洛又要几万次迭代才能把标准误压到 0.25pp——而翻前的输入只有
 * 169 类 × 8 个对手数共 1352 个数，离线算一次签入仓库，运行时迭代数为 0。
 *
 * 表的口径写死为「vs N 个**均匀随机**对手手牌」（§10.5 的默认模型）。
 * 带范围假设（top / weighted）或有 publicDead 时**不能**用这张表，
 * 调用方必须走蒙特卡洛——planner 负责这个分流。
 *
 * 表只有标准玩法（52 张）一份。短牌的底牌也是合法 CardId，查表不会报错，
 * 只会静默给出标准玩法的数——所以带 `variant` 的查询在短牌下一律返回 null。
 */
import type { CardId } from '../ids.js'
import type { DeckVariant } from '../poker/types.js'
import { HAND_CLASSES, classIndexOfCards } from './classes.js'
import TABLE from './tables/preflop-vs-random.json' with { type: 'json' }

/** JSON 的键是 169 个类名；用 string 索引要先擦掉字面量键类型。 */
const EQUITY_BY_CLASS = TABLE.equity as unknown as Record<string, readonly number[]>
const WIN_BY_CLASS = TABLE.win as unknown as Record<string, readonly number[]>
const TIE_BY_CLASS = TABLE.tie as unknown as Record<string, readonly number[]>

/** 万分比 → 小数，避免每次查表都做除法以外的事 */
const INV_BP = 1 / 10_000

export const PREFLOP_TABLE_META = Object.freeze({
  version: TABLE.version,
  headsUpMethod: TABLE.headsUpMethod,
  multiwaySamples: TABLE.multiwaySamples,
  seedBase: TABLE.seedBase,
  /** N=1 分层抽样的总发牌数：C(50,2) × 每组合 runout 数 */
  headsUpDeals: 1225 * 400,
})

/** 展平成 169 × 8 的 Float64Array，查表变成一次乘加寻址。 */
function flatten(src: Record<string, readonly number[]>, what: string): Float64Array {
  const flat = new Float64Array(169 * 8)
  for (let i = 0; i < 169; i++) {
    const row = src[HAND_CLASSES[i]!]
    if (!row || row.length !== 8) {
      // 表缺行属于构建期错误（生成脚本没跑或产物被改坏），必须早失败：
      // 悄悄返回 0 会让 AI 把 AA 当成必输牌。
      throw new Error(`preflop ${what} 表缺少手牌类 ${HAND_CLASSES[i]!}`)
    }
    for (let n = 0; n < 8; n++) flat[i * 8 + n] = row[n]! * INV_BP
  }
  return flat
}

const FLAT = flatten(EQUITY_BY_CLASS, 'equity')
const FLAT_WIN = flatten(WIN_BY_CLASS, 'win')
const FLAT_TIE = flatten(TIE_BY_CLASS, 'tie')

/**
 * 查表得到「vs `opponents` 个随机手牌」的 equity（含平局折半）。
 * `opponents` 必须是 1..8 的整数，否则返回 null 让调用方退回模拟。
 */
export function preflopEquityVsRandom(
  hole: readonly [CardId, CardId],
  opponents: number,
  variant: DeckVariant = 'standard',
): number | null {
  if (variant !== 'standard') return null // 还没有短牌表，绝不退回去查标准表
  if (!Number.isInteger(opponents) || opponents < 1 || opponents > 8) return null
  return FLAT[classIndexOfCards(hole[0], hole[1]) * 8 + (opponents - 1)]!
}

/**
 * win / tie 两个分量。EquityResult 契约要求 win + tie + lose === 1，
 * 只存 equity 的话这三个数就只能靠编——而「翻前平局率恒为 0」是明显的假话
 * （河牌打公共牌的平局在多路底池很常见）。
 */
export function preflopWinTie(
  hole: readonly [CardId, CardId],
  opponents: number,
  variant: DeckVariant = 'standard',
): { win: number; tie: number } | null {
  if (variant !== 'standard') return null
  if (!Number.isInteger(opponents) || opponents < 1 || opponents > 8) return null
  const idx = classIndexOfCards(hole[0], hole[1]) * 8 + (opponents - 1)
  return { win: FLAT_WIN[idx]!, tie: FLAT_TIE[idx]! }
}

/** 按类索引直接查，供 RANGE_ORDER 与测试使用 */
export function preflopEquityByClassIndex(classIndex: number, opponents: number): number {
  return FLAT[classIndex * 8 + (opponents - 1)]!
}

/**
 * 表值的标准误上界（用于 EquityResult.stdErr）。
 *
 * 表不是精确枚举，把 stdErr 报成 0 会让 UI 打上「精确」角标并去掉不确定区间，
 * 那是在撒谎。N=1 走分层抽样，这里给的是朴素 MC 的上界，实际远小于它。
 */
export function preflopStdErr(opponents: number): number {
  const n = opponents === 1 ? PREFLOP_TABLE_META.headsUpDeals : PREFLOP_TABLE_META.multiwaySamples
  return 0.5 / Math.sqrt(n)
}
